// DataTable (Components, BL-189 consolidation): records, one a row, to open (base.css's .flw-table, as
// Accounting › Invoices and Payments and refunds drew them). A head on the ink rule, rows on hairlines,
// numbers to the right; it scrolls in its own box when it's wider than the page. One column is the row's
// way in: with hrefOf its words are the row's link (600, underlined in ink) and a click anywhere in the
// row opens it; a row this person can't open isn't a link, and without hrefOf (a chat) no row opens. A
// cell is words, drawn from its DataCell: muted or strong, a state word (DESIGN.md §2: muted, dashed
// under while it waits, red for a problem), links (an invoice a payment paid), lines under it, struck
// through. What each record's columns say is the caller's (rows/money-columns.ts). Under the rows, the
// next page on asking (`more`). Reads only --dtable-*; Link reads its own.
import type { MouseEvent, ReactNode } from 'react';
import { Link } from '../Link/Link.tsx';
import './DataTable.css';

/** A state word, drawn as DESIGN.md §2 says: ink (the default), muted, dashed under for waiting, red. */
export interface DataWord {
  word: string;
  tone?: 'ink' | 'muted' | 'wait' | 'bad';
}

/** A line under a cell's words: muted (13 px), or red and strong for a problem ("3 days late"). */
export interface DataLine {
  text: ReactNode;
  tone?: 'bad';
}

export interface DataCell {
  /** The cell's words. */
  text?: ReactNode;
  /** muted: a value that isn't one yet ("Not issued"); strong: the figure that matters (what's owed). */
  look?: 'muted' | 'strong';
  /** A state word in place of the words. */
  word?: DataWord;
  /** Links, one after another with commas (the invoices a payment paid); without href, the words alone. */
  links?: readonly { id: string; text: string; href?: string | null }[];
  /** Lines under it; a line may be a state word. */
  lines?: readonly (DataLine | DataWord)[];
  /** Voided: struck through (and muted, where it isn't a link). */
  struck?: boolean;
}

export interface DataColumn<R> {
  id: string;
  label: string;
  /** A figure: to the right, never wrapping. */
  align?: 'end';
  /** The row's head (th scope=row): the number or the day that names the row. */
  rowHeader?: boolean;
  /** This column's words are the row's way in (its link, with hrefOf). */
  way?: boolean;
  cell: (row: R) => DataCell;
}

/** The next page of a list, read on asking: its words ("Show 50 more of 120"), while it reads, and why it didn't. */
export interface MoreRows {
  label: string;
  onPress: () => void;
  isBusy?: boolean;
  error?: string | null;
}

export interface DataTableProps<R extends { id: string }> {
  columns: readonly DataColumn<R>[];
  rows: readonly R[];
  /** Where a row opens, or null for one this person can't open; without it (a chat) no row opens. */
  hrefOf?: (row: R) => string | null;
  /** The next page, on asking. */
  more?: MoreRows | null;
  /** With no rows, the sentence it says between dashed rules ("No invoices here. Try another filter."). */
  empty: string;
}

/** A row opens as its own link does, wherever in the row it's clicked (not on another link or control). */
const openRow = (e: MouseEvent<HTMLTableRowElement>): void => {
  if ((e.target as Element).closest('a, button, input, label')) return;
  e.currentTarget.querySelector<HTMLAnchorElement>('a[data-open]')?.click();
};

const Word = ({ w }: { w: DataWord }) => (
  <span className="fl-dtable__word" data-tone={w.tone ?? 'ink'}>
    {w.word}
  </span>
);

function Words({ c, way, href }: { c: DataCell; way: boolean; href: string | null }) {
  const struck = c.struck ? '' : undefined;
  if (way)
    return href ? (
      <a className="fl-dtable__way" data-open="" data-struck={struck} href={href}>
        {c.text}
      </a>
    ) : (
      <b className="fl-dtable__strong" data-struck={struck}>
        {c.text}
      </b>
    );
  if (c.word) return <Word w={c.word} />;
  if (c.links)
    return (
      <>
        {c.links.map((l, k) => (
          <span key={l.id}>
            {k > 0 && ', '}
            {l.href ? (
              <a className="fl-dtable__link" href={l.href}>
                {l.text}
              </a>
            ) : (
              l.text
            )}
          </span>
        ))}
      </>
    );
  if (c.look === 'muted') return <span className="fl-dtable__muted">{c.text}</span>;
  if (c.look === 'strong') return <b className="fl-dtable__strong">{c.text}</b>;
  return <>{c.text}</>;
}

export function DataTable<R extends { id: string }>({
  columns,
  rows,
  hrefOf,
  more,
  empty,
}: DataTableProps<R>) {
  if (!rows.length)
    return (
      <p className="fl-dtable__empty" role="status">
        {empty}
      </p>
    );
  return (
    <div className="fl-dtable">
      <div className="fl-dtable__wrap">
        <table className="fl-dtable__t">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.id} scope="col" data-num={col.align === 'end' ? '' : undefined}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const href = hrefOf?.(r) ?? null;
              return (
                <tr key={r.id} data-open={href ? '' : undefined} onClick={href ? openRow : undefined}>
                  {columns.map((col) => {
                    const c = col.cell(r);
                    const body = (
                      <>
                        <Words c={c} way={!!col.way} href={href} />
                        {c.lines?.map((l, i) =>
                          'word' in l ? (
                            <span key={i} className="fl-dtable__sub">
                              <Word w={l} />
                            </span>
                          ) : (
                            <span key={i} className="fl-dtable__sub" data-tone={l.tone}>
                              {l.text}
                            </span>
                          ),
                        )}
                      </>
                    );
                    const num = col.align === 'end' ? '' : undefined;
                    return col.rowHeader ? (
                      <th key={col.id} scope="row" data-num={num}>
                        {body}
                      </th>
                    ) : (
                      <td key={col.id} data-num={num}>
                        {body}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {more ? (
        <p className="fl-dtable__more">
          <Link onPress={more.onPress} isDisabled={!!more.isBusy}>
            {more.isBusy ? 'Reading…' : more.label}
          </Link>
          {more.error ? (
            <span className="fl-dtable__alert" role="alert">
              {more.error}
            </span>
          ) : null}
        </p>
      ) : null}
    </div>
  );
}
